import { payTalkWalletRecharge } from "../../services/talkWalletPayment";
import { formatCallCreditRules, parseQuotaCredits } from "../../utils/callCreditRules";
import { showToast } from "../../utils/toast";
import "./CallCreditGateModal.css";

export default function CallCreditGateModal({
  mode,
  quota,
  peerName,
  busy,
  onBusyChange,
  onConfirm,
  onCancel,
  onTopUpSuccess,
  onNavigateManage,
}) {
  if (!mode) return null;

  const { remaining, total, reserved, thresholdMinutes } = parseQuotaCredits(quota);
  const rules = formatCallCreditRules(thresholdMinutes);
  const name = peerName || "this caregiver";
  const rechargePrice = Number(quota?.rechargePriceInr) || 50;
  const rechargeMins = Number(quota?.rechargeMinutesGranted) || 15;

  async function handleTopUp() {
    if (busy) return;
    onBusyChange?.(true);
    try {
      const result = await payTalkWalletRecharge();
      showToast.success(`Talk wallet topped up${rechargeMins ? ` (+${rechargeMins} mins)` : ""}.`);
      onTopUpSuccess?.(result?.quota || null);
    } catch (err) {
      if (err?.message !== "Payment cancelled") {
        showToast.error(err?.message || "Unable to complete top-up.");
      }
    } finally {
      onBusyChange?.(false);
    }
  }

  const isExhausted = mode === "exhausted";

  return (
    <div className="call-credit-gate__backdrop" role="dialog" aria-modal="true" aria-labelledby="call-credit-gate-title">
      <div className="call-credit-gate__modal">
        <p className="call-credit-gate__eyebrow">Discovery calls</p>
        <h2 id="call-credit-gate-title" className="call-credit-gate__title">
          {isExhausted ? "Call credits used up" : rules.confirmTitle}
        </h2>

        <p className="call-credit-gate__body">
          {isExhausted
            ? `You have no call credits left to call ${name}. Top up your talk wallet to keep interviewing caregivers on NEMA.`
            : rules.confirmBody(remaining, reserved)}
        </p>

        <div className="call-credit-gate__stats" aria-label="Call credit balance">
          <div className="call-credit-gate__stat">
            <span>Available</span>
            <strong>{remaining}</strong>
          </div>
          <div className="call-credit-gate__stat">
            <span>Total</span>
            <strong>{total}</strong>
          </div>
          <div className="call-credit-gate__stat">
            <span>Reserved</span>
            <strong>{reserved}</strong>
          </div>
        </div>

        <p className="call-credit-gate__rule">{rules.shortRule}</p>

        <div className="call-credit-gate__actions">
          {isExhausted ? (
            <>
              <button
                type="button"
                className="call-credit-gate__btn call-credit-gate__btn--primary"
                disabled={busy}
                onClick={handleTopUp}
              >
                {busy ? "Processing…" : `Top up — ₹${rechargePrice} (+${rechargeMins} mins)`}
              </button>
              {onNavigateManage ? (
                <button
                  type="button"
                  className="call-credit-gate__btn call-credit-gate__btn--ghost"
                  onClick={onNavigateManage}
                >
                  View call history &amp; credits
                </button>
              ) : null}
              <button type="button" className="call-credit-gate__btn call-credit-gate__btn--link" onClick={onCancel}>
                Not now
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="call-credit-gate__btn call-credit-gate__btn--primary"
                disabled={busy}
                onClick={onConfirm}
              >
                {busy ? "Starting…" : "Start call (uses 1 credit)"}
              </button>
              <button type="button" className="call-credit-gate__btn call-credit-gate__btn--ghost" onClick={onCancel}>
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
